// File: src/components/feeds/AnnouncementFeed/AnnouncementCard.tsx

import React from "react";
import styled, { keyframes } from "styled-components";
import { AlertTriangle, Info, CheckCircle, XCircle, X } from "lucide-react";
import { Typography, Flex, IconButton, Card } from "../../../components";

export type AnnouncementType = "info" | "warning" | "success" | "error";

export interface Announcement {
  id: string;
  message: string;
  timestamp?: string;
  type?: AnnouncementType;
  dismissible?: boolean;
}
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledCard = styled(Card)<{ type?: AnnouncementType }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.radii.md};
  animation: ${slideUp} 0.3s ease-in-out;

  background-color: ${({ theme, type }) => {
    switch (type) {
      case "success": return theme.colors.success + "20";
      case "warning": return theme.colors.warning + "20";
      case "error": return theme.colors.error + "20";
      default: return theme.colors.surface;
    }
  }};

  border-left: 4px solid ${({ theme, type }) => {
    switch (type) {
      case "success": return theme.colors.success;
      case "warning": return theme.colors.warning;
      case "error": return theme.colors.error;
      default: return theme.colors.primary;
    }
  }};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: flex-start;
    padding: ${({ theme }) => theme.spacing.sm};
    gap: ${({ theme }) => theme.spacing.xs};
    border: none;
    border-radius:0;
  }
`;

const iconMap = {
  info: <Info size={18} />,
  warning: <AlertTriangle size={18} />,
  success: <CheckCircle size={18} />,
  error: <XCircle size={18} />,
};

interface AnnouncementCardProps {
  item: Announcement;
  onDismiss: (id: string) => void;
}

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({ item, onDismiss }) => {
  return (
    <StyledCard type={item.type}>
      {iconMap[item.type || "info"]}
      <Flex direction="column" flex="1">
        <Typography variant="body" as="span">{item.message}</Typography>
        {item.timestamp && (
          <Typography variant="caption" as="time" colorScheme="muted">
            {item.timestamp}
          </Typography>
        )}
      </Flex>
      {item.dismissible && (
        <IconButton
          icon={<X size={18} />}
          ariaLabel="Dismiss"
          variant="ghost"
          size="small"
          onClick={() => onDismiss(item.id)}
        />
      )}
    </StyledCard>
  );
};